import { FC, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import WidgetsOutlinedIcon from '@mui/icons-material/WidgetsOutlined';
import SaveIcon from '@mui/icons-material/Save';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { SCUIContent } from '../../atomic/organisms';
import { SCUIBreadCrumb } from '../../atomic/molecules';
import { SCUIButton, SCUITextField } from '../../atomic/atoms';
import RuntimeConfigurationCommand from '../../services/command/runtimeConfiguration/runtimeConfigurationCommand.services';
import { PageUrl } from '../../types/enums/pageUrl-enums';
import {
    RuntimeConfigurationInputMode,
    RuntimeConfigurationValueType,
} from '../../types/enums/runtime-configuration-enums';
import { RuntimeConfigurationValueEditor } from './components/RuntimeConfigurationValueEditor';
import { RuntimeConfigurationValueTypePicker } from './components/RuntimeConfigurationValueTypePicker';
import {
    buildRuntimeConfigurationDetailUrl,
    defaultValueForType,
    getRuntimeConfigurationOperationMessage,
    isJsonValueType,
    isRuntimeConfigurationOperationFailed,
    JsonValidationResult,
    toPayloadValue,
} from './runtimeConfiguration.helpers';
import './components/RuntimeConfigurationValueEditor.scss';
import './runtime-configuration-pages.scss';

export const RuntimeConfigurationCreate: FC = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const [key, setKey] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [description, setDescription] = useState('');
    const [valueType, setValueType] = useState<RuntimeConfigurationValueType>(RuntimeConfigurationValueType.String);
    const [value, setValue] = useState<unknown>(() => defaultValueForType(RuntimeConfigurationValueType.String));
    const [saving, setSaving] = useState(false);
    const [jsonValidation, setJsonValidation] = useState<JsonValidationResult>({ valid: true, error: null });
    const hasInvalidJson = isJsonValueType(valueType) && !jsonValidation.valid;

    const handleValueTypeChange = (next: RuntimeConfigurationValueType) => {
        setValueType(next);
        setValue(defaultValueForType(next));
    };

    const goToList = () => history.push(PageUrl.runtimeConfiguration.url);

    const handleSave = async () => {
        if (hasInvalidJson) return;
        setSaving(true);
        try {
            const result = await RuntimeConfigurationCommand.CreateRuntimeConfiguration({
                key: key.trim(),
                displayName: displayName.trim(),
                description: description.trim() || undefined,
                valueType,
                inputMode: RuntimeConfigurationInputMode.Auto,
                initialValue: toPayloadValue(value, valueType),
            });

            if (isRuntimeConfigurationOperationFailed(result)) {
                Swal.fire({
                    icon: 'error',
                    title: t('general.error'),
                    text: getRuntimeConfigurationOperationMessage(result, t('general.error')),
                });
                return;
            }

            const createdKey = result.key ?? result.Key ?? key.trim();
            const createdVersion = result.currentVersion ?? result.CurrentVersion ?? 1;

            await Swal.fire({
                icon: 'success',
                title: t('general.success'),
                timer: 1500,
                showConfirmButton: false,
            });
            history.push(buildRuntimeConfigurationDetailUrl(createdKey, createdVersion));
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : t('general.error');
            Swal.fire({ icon: 'error', title: t('general.error'), text: message });
        } finally {
            setSaving(false);
        }
    };

    return (
        <SCUIContent>
            <Box className="rc-page">
                <Box className="rc-page__breadcrumb">
                    <SCUIBreadCrumb
                        onlyTitle={t('runtimeConfiguration.create.title')}
                        components={[
                            { title: t('runtimeConfiguration.title'), link: PageUrl.runtimeConfiguration.url },
                            { title: t('runtimeConfiguration.create.title') },
                        ]}
                    />
                </Box>

                <Box className="rc-page__header">
                    <Box>
                        <h1 className="rc-page__title">{t('runtimeConfiguration.create.pageTitle')}</h1>
                        <p className="rc-page__subtitle">{t('runtimeConfiguration.create.pageSubtitle')}</p>
                    </Box>
                    <Box className="rc-page__header-actions">
                        <SCUIButton type="secondary" text={t('general.cancel')} onClick={goToList} />
                    </Box>
                </Box>

                <Box className="rc-card">
                    <Box className="rc-card__header">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box className="rc-card__header-icon">
                                <WidgetsOutlinedIcon sx={{ fontSize: 18 }} />
                            </Box>
                            <Box>
                                <h2 className="rc-card__header-title">{t('runtimeConfiguration.create.cardTitle')}</h2>
                                <p className="rc-card__header-subtitle">{t('runtimeConfiguration.create.cardSubtitle')}</p>
                            </Box>
                        </Box>
                        <span className="rc-badge rc-badge--start">{t('runtimeConfiguration.create.startVersion')}</span>
                    </Box>

                    <Box className="rc-card__body">
                        <Box className="rc-form-stack">
                            <Box>
                                <label className="rc-field-label" htmlFor="rc-create-key">
                                    {t('runtimeConfiguration.fields.key')} *
                                </label>
                                <SCUITextField
                                    value={key}
                                    placeholder={t('runtimeConfiguration.fields.keyPlaceholder')}
                                    onChange={(textValue: string) => setKey(textValue)}
                                />
                            </Box>

                            <Box className="rc-form-grid">
                                <Box>
                                    <label className="rc-field-label" htmlFor="rc-create-display-name">
                                        {t('runtimeConfiguration.fields.displayName')} *
                                    </label>
                                    <SCUITextField
                                        id="rc-create-display-name"
                                        value={displayName}
                                        placeholder={t('runtimeConfiguration.fields.displayNamePlaceholder')}
                                        helperText={t('runtimeConfiguration.fields.displayNameHelp')}
                                        onChange={(textValue: string) => setDisplayName(textValue)}
                                    />
                                </Box>
                            </Box>

                            <Box>
                                <label className="rc-field-label" htmlFor="rc-create-description">
                                    {t('runtimeConfiguration.fields.description')}
                                </label>
                                <SCUITextField
                                    id="rc-create-description"
                                    value={description}
                                    multiline
                                    placeholder={t('runtimeConfiguration.fields.descriptionPlaceholder')}
                                    helperText={t('runtimeConfiguration.fields.descriptionHelp')}
                                    onChange={(textValue: string) => setDescription(textValue)}
                                />
                            </Box>

                            <Box className="rc-value-section">
                                <RuntimeConfigurationValueTypePicker
                                    value={valueType}
                                    onChange={handleValueTypeChange}
                                />
                                <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                                    {t(`runtimeConfiguration.typeHelp.${valueType}`)}
                                </Typography>
                                <RuntimeConfigurationValueEditor
                                    valueType={valueType}
                                    value={value}
                                    onChange={setValue}
                                    onValidationChange={setJsonValidation}
                                />
                            </Box>
                        </Box>

                        <Box className="rc-form-actions">
                            <SCUIButton type="secondary" text={t('general.cancel')} onClick={goToList} />
                            <SCUIButton
                                type="primary"
                                text={t('runtimeConfiguration.create.saveAndPublish')}
                                showStartIcon
                                startIcon={<SaveIcon sx={{ fontSize: 18 }} />}
                                disabled={saving || !key.trim() || !displayName.trim() || hasInvalidJson}
                                onClick={handleSave}
                            />
                        </Box>
                    </Box>
                </Box>

            </Box>
        </SCUIContent>
    );
};
